import type { Asset } from '@/types/asset';

export interface DepreciationCalculationResult {
  depreciationValue: number;
  closingNBV: number;
  method: string;
  formula: string;
  assumptions: string[];
  depreciationPercent?: number;
}

export class DepreciationCalculator {
  /**
   * Calculate depreciation for an asset based on its depreciation method
   */
  static calculateDepreciation(
    asset: Asset,
    periodDate: Date = new Date(),
    periodsPerYear: number = 12
  ): DepreciationCalculationResult {
    const method = asset.depreciationMethod || 'Straight Line';

    // Handle "None" method
    if (method.toLowerCase() === 'none') {
      return {
        depreciationValue: 0,
        closingNBV: asset.netBookValue,
        method: 'None',
        formula: 'No depreciation applied',
        assumptions: ['Asset does not depreciate'],
        depreciationPercent: 0,
      };
    }

    const depreciableAmount = asset.netBookValue - (asset.residualValue || 0);
    const usefulLife = asset.usefulLife || 5; // Default to 5 years if not specified
    const totalPeriods = usefulLife * periodsPerYear;

    // Calculate periods elapsed since acquisition
    const periodsElapsed = this.calculatePeriodsElapsed(
      new Date(asset.acquisitionDate),
      periodDate,
      periodsPerYear
    );

    switch (method) {
      case 'Straight Line':
        return this.calculateStraightLine(asset, depreciableAmount, totalPeriods, periodsPerYear);

      case 'Reducing Balance':
      case 'Declining Balance':
        return this.calculateReducingBalance(
          asset,
          depreciableAmount,
          usefulLife,
          periodsPerYear,
          periodsElapsed
        );

      case 'Sum of Years Digits':
      case 'Sum of Digits':
        return this.calculateSumOfDigits(
          asset,
          depreciableAmount,
          usefulLife,
          periodsPerYear,
          periodsElapsed
        );

      case 'Period Ratio':
        return this.calculatePeriodRatio(asset, depreciableAmount, totalPeriods, periodsElapsed);

      default:
        // Default to Straight Line if method not recognized
        const result = this.calculateStraightLine(
          asset,
          depreciableAmount,
          totalPeriods,
          periodsPerYear
        );
        result.assumptions.push(`Unknown method '${method}', defaulted to Straight Line`);
        return result;
    }
  }

  private static calculateStraightLine(
    asset: Asset,
    depreciableAmount: number,
    totalPeriods: number,
    periodsPerYear: number
  ): DepreciationCalculationResult {
    const depreciationPerPeriod = totalPeriods > 0 ? depreciableAmount / totalPeriods : 0;

    return {
      depreciationValue: depreciationPerPeriod,
      closingNBV: asset.netBookValue - depreciationPerPeriod,
      method: 'Straight Line',
      formula: `(Cost - Residual Value) / Useful Life / ${periodsPerYear}`,
      depreciationPercent:
        asset.netBookValue > 0 ? (depreciationPerPeriod / asset.netBookValue) * 100 : 0,
      assumptions: [
        'Depreciates evenly over useful life',
        `Useful life: ${asset.usefulLife || 5} years`,
        `Residual value: £${(asset.residualValue || 0).toFixed(2)}`,
      ],
    };
  }

  private static calculateReducingBalance(
    asset: Asset,
    depreciableAmount: number,
    usefulLife: number,
    periodsPerYear: number,
    periodsElapsed: number
  ): DepreciationCalculationResult {
    // Calculate annual rate
    let annualRate: number;
    if (asset.residualValue && asset.residualValue > 0 && asset.acquisitionCost > 0) {
      // More accurate formula: rate = 1 - (residual/cost)^(1/life)
      const ratio = asset.residualValue / asset.acquisitionCost;
      annualRate = 1 - Math.pow(ratio, 1 / usefulLife);
    } else {
      // Simplified: 200% declining balance (double declining)
      annualRate = 2.0 / usefulLife;
    }

    const periodRate = annualRate / periodsPerYear;
    let depreciationValue = asset.netBookValue * periodRate;

    // Ensure we don't depreciate below residual value
    const residualValue = asset.residualValue || 0;
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Reducing Balance',
      formula: `Book Value × ${(annualRate * 100).toFixed(2)}% / ${periodsPerYear}`,
      depreciationPercent:
        asset.netBookValue > 0 ? (depreciationValue / asset.netBookValue) * 100 : 0,
      assumptions: [
        'Depreciates a fixed percentage of book value each period',
        `Annual rate: ${(annualRate * 100).toFixed(2)}%`,
        `Period rate: ${(periodRate * 100).toFixed(2)}%`,
        `Residual value: £${residualValue.toFixed(2)}`,
      ],
    };
  }

  private static calculateSumOfDigits(
    asset: Asset,
    depreciableAmount: number,
    usefulLife: number,
    periodsPerYear: number,
    periodsElapsed: number
  ): DepreciationCalculationResult {
    // Sum of Years Digits method
    const totalPeriods = usefulLife * periodsPerYear;
    const sumOfDigits = (totalPeriods * (totalPeriods + 1)) / 2;

    // Remaining life for this period
    let remainingPeriods = totalPeriods - periodsElapsed;
    remainingPeriods = Math.max(1, remainingPeriods); // At least 1

    // Depreciation for this period
    let depreciationValue = sumOfDigits > 0 ? (depreciableAmount * remainingPeriods) / sumOfDigits : 0;

    // Ensure we don't depreciate below residual value
    const residualValue = asset.residualValue || 0;
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Sum of Years Digits',
      formula: `(Cost - Residual) × (Remaining Life / Sum of Digits)`,
      depreciationPercent:
        asset.netBookValue > 0 ? (depreciationValue / asset.netBookValue) * 100 : 0,
      assumptions: [
        'Accelerated depreciation based on remaining life',
        `Total periods: ${totalPeriods}`,
        `Sum of digits: ${sumOfDigits}`,
        `Remaining periods: ${remainingPeriods}`,
        `Current period fraction: ${remainingPeriods}/${sumOfDigits}`,
      ],
    };
  }

  private static calculatePeriodRatio(
    asset: Asset,
    depreciableAmount: number,
    totalPeriods: number,
    periodsElapsed: number
  ): DepreciationCalculationResult {
    // Period Ratio is similar to Sum of Digits but uses period weights
    const sumOfPeriods = (totalPeriods * (totalPeriods + 1)) / 2;
    let remainingPeriods = totalPeriods - periodsElapsed;
    remainingPeriods = Math.max(1, remainingPeriods);

    const periodWeight = remainingPeriods;
    let depreciationValue = sumOfPeriods > 0 ? (depreciableAmount * periodWeight) / sumOfPeriods : 0;

    // Ensure we don't depreciate below residual value
    const residualValue = asset.residualValue || 0;
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Period Ratio',
      formula: `(Cost - Residual) × (Period Weight / Sum of Weights)`,
      depreciationPercent:
        asset.netBookValue > 0 ? (depreciationValue / asset.netBookValue) * 100 : 0,
      assumptions: [
        'Depreciation based on period weight ratio',
        `Total periods: ${totalPeriods}`,
        `Sum of period weights: ${sumOfPeriods}`,
        `Current period weight: ${periodWeight}`,
        `Period fraction: ${periodWeight}/${sumOfPeriods}`,
      ],
    };
  }

  private static calculatePeriodsElapsed(
    acquisitionDate: Date,
    periodDate: Date,
    periodsPerYear: number
  ): number {
    const monthsElapsed =
      (periodDate.getFullYear() - acquisitionDate.getFullYear()) * 12 +
      periodDate.getMonth() -
      acquisitionDate.getMonth();

    if (periodsPerYear === 12) {
      return Math.max(0, monthsElapsed);
    } else if (periodsPerYear === 4) {
      // Quarterly
      return Math.max(0, Math.floor(monthsElapsed / 3));
    } else if (periodsPerYear === 1) {
      // Annually
      return Math.max(0, Math.floor(monthsElapsed / 12));
    } else {
      // Default to monthly
      return Math.max(0, monthsElapsed);
    }
  }
}
